import { Link } from "@/lib/nav";

import CustomerFooter from "@/components/customer-footer";
import CustomerScreenShell from "@/components/customer-screen-shell";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { formatDateTime } from "@buildpy/core/lib/format";
import { customerStatementPath } from "@buildpy/core/modules/change-order/current-statement";
import { CHANGE_ORDER_RESEND_PENDING_MESSAGE } from "@buildpy/core/modules/change-order/messages";
import ChangeOrderAcceptForm from "@/modules/change-order/ui/change-order-accept-form";
import ChangeOrderDocument from "@/modules/change-order/ui/change-order-document";
import ChangeOrderStatusBadge from "@/modules/change-order/ui/status-badge";
import Money from "@/components/money";
import { shortHash } from "@buildpy/core/modules/contract/customer-link-model";
import { SIGNED_RECORD_NOTICE } from "@buildpy/core/modules/contract/customer-link-text";
import { verifyContractShare } from "@/api/actions/contract-share";
import ContractSignForm from "@/modules/contract/ui/contract-sign-form";
import UnusableLink from "@/modules/contract/ui/unusable-link";
import A4Fit from "@/modules/print/a4-fit";
import ContractDocument from "@/modules/print/contract-document";
import PrintButton from "@/modules/print/print-button";
import { customerProjectPath } from "@buildpy/core/modules/project/customer-visible";
import ShareGate from "@/modules/share/ui/share-gate";
import CustomerHeadOrg from "@/modules/share/ui/customer-head-org";
import type { CustomerChangeOrder, CustomerContractView } from "@buildpy/core/views/g2b/customer-contract";

interface Props {
  data: CustomerContractView;
  token: string;
}

// 체결 뒤 변경공사 — 보낸 건은 문서와 합의 폼(지급 기록이 바뀌었으면 안내만), 결정된 건은 기록과 문서를 읽기 전용으로.
const ChangeOrderSection = ({
  token,
  changeOrders,
  staleIds,
}: {
  token: string;
  changeOrders: { change_order: CustomerChangeOrder }[];
  staleIds: ReadonlySet<string>;
}) => (
  <section
    className="print-hide mx-auto mt-4 flex w-full max-w-3xl scroll-mt-16 flex-col gap-y-4 px-2"
    data-testid="change-order-section"
    id="change-orders">
    <h2 className="px-2 text-base font-semibold">변경공사</h2>
    {changeOrders.map(({ change_order: co }) => (
      <SectionCard
        key={co.id}
        as="article"
        className="flex flex-col gap-y-3"
        padding="sm"
        data-testid={`change-order-${co.status.toLowerCase()}`}>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <ChangeOrderStatusBadge status={co.status} />
          {/* 폰에서 줄이 붙어 읽히지 않게 — 상태·제목·금액 사이 낭독 구분자. */}
          <span className="sr-only">, </span>
          <span className="font-semibold">
            변경 {co.seq}차 · {co.title}
          </span>
          <span className="sr-only">, 증감 </span>
          <Money className="text-muted-foreground" signed value={co.delta_total} />
        </div>
        {co.status === "SENT" && staleIds.has(co.id) ? (
          <p className="text-sm text-muted-foreground" data-testid="change-order-resend-pending">
            {CHANGE_ORDER_RESEND_PENDING_MESSAGE}
          </p>
        ) : co.status === "SENT" ? (
          <>
            {co.snapshot_data && (
              <A4Fit>
                <ChangeOrderDocument data={co.snapshot_data} docHash={co.doc_hash} draft={false} />
              </A4Fit>
            )}
            <ChangeOrderAcceptForm changeOrderId={co.id} docHash={co.doc_hash} token={token} />
          </>
        ) : (
          <>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="text-muted-foreground">{co.status === "ACCEPTED" ? "합의 시각" : "거절 시각"}</dt>
              <dd className="tabular-nums">{formatDateTime(co.decided_at)}</dd>
              {co.decided_name && (
                <>
                  <dt className="text-muted-foreground">이름</dt>
                  <dd>{co.decided_name}</dd>
                </>
              )}
              {co.status === "ACCEPTED" && co.signature_path && (
                <>
                  <dt className="text-muted-foreground">서명</dt>
                  <dd>
                    <img
                      alt="도급인 서명"
                      className="h-16 w-auto rounded-sm border border-border"
                      data-testid="change-order-signature"
                      loading="lazy"
                      src={`/api${co.signature_path}`}
                    />
                  </dd>
                </>
              )}
              {co.status === "ACCEPTED" && co.after_total !== null && (
                <>
                  <dt className="text-muted-foreground">변경 후 계약금액</dt>
                  <dd className="tabular-nums"><Money value={co.after_total} /></dd>
                </>
              )}
              {co.status === "ACCEPTED" && (
                <>
                  <dt className="text-muted-foreground">문서 해시</dt>
                  <dd className="font-mono">{shortHash(co.doc_hash)}…</dd>
                </>
              )}
            </dl>
            {co.status === "ACCEPTED" && co.snapshot_data && (
              <details className="text-sm">
                <summary className="tap-target relative cursor-pointer text-muted-foreground">변경계약서 보기</summary>
                <div className="mt-2">
                  <A4Fit>
                    <ChangeOrderDocument data={co.snapshot_data} docHash={co.doc_hash} draft={false} />
                  </A4Fit>
                </div>
              </details>
            )}
          </>
        )}
      </SectionCard>
    ))}
  </section>
);

// 고객이 보는 계약서 — 토큰 + 휴대폰 뒷 4자리가 열쇠이고, SENT 면 체결 폼, 체결됐으면 체결본과 기록을 보여 준다.
const Page = ({ data, token }: Props) => {
  if (data.kind === "unusable") return <UnusableLink />;

  const { orgName } = data;
  if (data.kind === "gate") {
    return (
      <CustomerScreenShell>
        <ShareGate docName="계약서" orgName={orgName} token={token} verify={verifyContractShare} />
      </CustomerScreenShell>
    );
  }

  const { state, company, contract, changeOrders, hasProject, model } = data;
  const staleIds = new Set(data.staleIds);

  return (
    <CustomerScreenShell className="p-0">
      <header className="print-hide sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-4 pb-2.5 pt-safe-2.5">
        <CustomerHeadOrg name={orgName} tel={company.tel} />
        <PrintButton label="PDF 저장(인쇄)" variant="outline" />
      </header>

      {state === "signed" && (
        <SectionCard
          className="print-hide mx-auto mt-4 w-full max-w-md text-sm"
          data-testid="contract-signed-record">
          <h2 className="text-base font-semibold">체결 완료 · 전자적 합의 기록</h2>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <dt className="text-muted-foreground">체결 시각</dt>
            <dd className="tabular-nums">{formatDateTime(contract.signed_at)}</dd>
            <dt className="text-muted-foreground">서명한 이름</dt>
            <dd>{contract.signed_name}</dd>
            <dt className="text-muted-foreground">문서 해시</dt>
            <dd className="font-mono">{shortHash(contract.signed_doc_hash)}…</dd>
          </dl>
          <p className="mt-2 text-xs text-muted-foreground">{SIGNED_RECORD_NOTICE}</p>
          {hasProject && (
            <Button asChild className="mt-3 w-full" variant="outline">
              <Link data-testid="customer-project-link" href={customerProjectPath(token)}>
                내 현장 보기
              </Link>
            </Button>
          )}
          <Button asChild className={hasProject ? "mt-2 w-full" : "mt-3 w-full"} variant="outline">
            <Link data-testid="customer-statement-link" href={customerStatementPath(token)}>
              현재 내역서 보기
            </Link>
          </Button>
        </SectionCard>
      )}

      {changeOrders.length > 0 && <ChangeOrderSection changeOrders={changeOrders} staleIds={staleIds} token={token} />}

      <div className="print-root px-2 py-4 md:px-4">
        <A4Fit>
          <ContractDocument model={model} />
        </A4Fit>
      </div>

      {state === "open" && (
        <div className="px-4 pb-8">
          <ContractSignForm token={token} />
        </div>
      )}
      <CustomerFooter className="mt-0 px-4 pb-8" show={data.footer} />
    </CustomerScreenShell>
  );
};

export default Page;
